import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Layout from "../../../component/layout";
import TankGauge from "./components/tank_gauge";
import LevelChart from "./components/level_chart";
import { StatusBadge, SignalBars, BatteryLevel } from "./components/tank_widgets";
import { updateSpinner } from "../../../../../features/authentication/authenticationReducer";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { getItem } from "../../../../../utils/localStorage";
import { useFacilityStore } from "../../../../../app/z-store/store";
import { backend_url, waterSensorTankURL, getWaterSensorAlertsURL } from "../../../../../utils/urls";
import {
  ALERT_BADGES,
  ALERT_LABELS,
  formatDateTime,
  formatLitres,
  formatMetres,
  formatPercent,
  tankStatus,
  timeAgo,
} from "./utils/format";
import { WATER_SENSORS_PATH, waterSensorsTabPath } from "./utils/routes";
import "./water_sensors.css";

const RANGES = ["6h", "24h", "7d", "30d", "90d"];
const REFRESH_MS = 60000;

const SETTINGS_FIELDS = [
  { key: "name", label: "Tank name", type: "text" },
  { key: "location", label: "Location", type: "text" },
  { key: "tankDepthM", label: "Full water depth (m)", type: "number", step: "0.01", hint: "Water depth above the probe when the tank is full" },
  { key: "sensorOffsetM", label: "Probe height above floor (m)", type: "number", step: "0.01", hint: "0 if the probe rests on the tank floor" },
  { key: "capacityLitres", label: "Capacity (litres)", type: "number", step: "1", hint: "Optional — shows volume" },
  { key: "lowPct", label: "Low alarm (%)", type: "number", step: "1" },
  { key: "highPct", label: "High alarm (%)", type: "number", step: "1" },
  { key: "batteryLowPct", label: "Battery alarm (%)", type: "number", step: "1" },
];

function ViewTank() {
  const { tankId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const facilityId = useFacilityStore((state) => state.facilityId);
  const tankURL = waterSensorTankURL(facilityId, tankId);

  const [tank, setTank] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [range, setRange] = useState("24h");
  const [points, setPoints] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editForm, setEditForm] = useState({});

  const getTank = async () => {
    if (!facilityId) return;
    const response = await makeRequest2(tankURL, "GET");
    if (response.success) {
      setTank(response.data.data);
      setNotFound(false);
    } else if (/not found/i.test(response.error || "")) {
      setTank(null);
      setNotFound(true); // tank is on a gateway that isn't assigned to this facility
    } else {
      toastify(response.error, "error");
    }
  };

  const getReadings = async (selectedRange = range) => {
    if (!facilityId) return;
    const response = await makeRequest2(`${tankURL}/readings?range=${selectedRange}`, "GET");
    if (response.success) setPoints(response.data.data?.points || []);
    else toastify(response.error, "error");
    setChartLoading(false);
  };

  const getAlerts = async () => {
    if (!facilityId) return;
    const response = await makeRequest2(`${getWaterSensorAlertsURL(facilityId)}?tankId=${tankId}&limit=10`, "GET");
    if (response.success) setAlerts(response.data.alerts || []);
  };

  useEffect(() => {
    getTank();
    getAlerts();
    const refresh = setInterval(() => {
      getTank();
      getAlerts();
    }, REFRESH_MS);
    return () => clearInterval(refresh);
  }, [tankId, facilityId]);

  useEffect(() => {
    setChartLoading(true);
    getReadings(range);
    const refresh = setInterval(() => getReadings(range), REFRESH_MS);
    return () => clearInterval(refresh);
  }, [tankId, range, facilityId]);

  const openEditModal = () => {
    const form = {};
    SETTINGS_FIELDS.forEach((f) => (form[f.key] = tank[f.key] ?? ""));
    form.alertsEnabled = tank.alertsEnabled !== false;
    setEditForm(form);
    setShowEditModal(true);
  };

  const handleSaveSettings = async () => {
    const body = { ...editForm };
    SETTINGS_FIELDS.filter((f) => f.type === "number").forEach((f) => {
      body[f.key] = editForm[f.key] === "" ? "" : Number(editForm[f.key]);
    });

    dispatch(updateSpinner(true));
    const response = await makeRequest2(tankURL, "PATCH", body);
    dispatch(updateSpinner(false));

    if (response.success) {
      setTank(response.data.data);
      setShowEditModal(false);
      getAlerts();
      toastify("Tank updated successfully", "success");
    } else {
      toastify(response.error, "error");
    }
  };

  const handleDeleteTank = async () => {
    dispatch(updateSpinner(true));
    const response = await makeRequest2(tankURL, "DELETE");
    dispatch(updateSpinner(false));

    if (response.success) {
      toastify("Tank deleted", "success");
      navigate(waterSensorsTabPath("tanks"));
    } else {
      toastify(response.error, "error");
    }
  };

  // CSV comes through backend_main, which needs the Authorization header, so download it as a blob
  const handleExport = async () => {
    try {
      const user = await getItem("APPUSER");
      const response = await axios.get(`${backend_url}${tankURL}/readings/export?range=${range}`, {
        headers: { Authorization: `Bearer ${user?.authToken}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(tank?.name || "tank").replace(/[^\w-]+/g, "_")}_${range}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastify("Failed to download CSV", "error");
    }
  };

  if (notFound) {
    return (
      <Layout>
        <div className="water-sensors">
          <div className="card">
            <div className="card-body text-center py-5">
              <h5>Tank not found in this facility</h5>
              <p className="text-muted">It may be on a gateway assigned to another facility, or it was deleted.</p>
              <Link to={waterSensorsTabPath("tanks")} className="btn btn-outline-primary btn-sm">
                Back to tanks
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!tank) {
    return (
      <Layout>
        <div className="water-sensors">
          <div className="card">
            <div className="card-body text-center text-muted py-5">Loading…</div>
          </div>
        </div>
      </Layout>
    );
  }

  const status = tankStatus(tank);

  return (
    <Layout>
      <div className="water-sensors">
        <div className="page-header">
          <div className="page-block">
            <div className="row align-items-center">
              <div className="col-md-12">
                <ul className="breadcrumb mb-3">
                  <li className="breadcrumb-item">
                    <Link to="/facility">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/facility/utility_management/meter_types">Meters Management</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={WATER_SENSORS_PATH}>Water Level Sensors</Link>
                  </li>
                  <li className="breadcrumb-item active">
                    <span>{tank.name}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <div>
                  <Link to={waterSensorsTabPath("tanks")}>
                    <i className="ti ti-arrow-narrow-left"></i> Back
                  </Link>
                  <h4 className="mb-0 mt-1">{tank.name}</h4>
                  <div className="text-muted small">
                    {tank.location || "No location set"} · Slot {tank.slot} on {tank.gatewayId}
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <StatusBadge status={status} />
                  <button className="btn btn-primary btn-sm" onClick={openEditModal}>
                    <i className="ti ti-pencil me-1"></i> Edit settings
                  </button>
                  <button className="btn btn-outline-danger btn-sm" onClick={() => setShowDeleteModal(true)}>
                    <i className="ti ti-trash me-1"></i> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Current level</h5>
              </div>
              <div className="card-body d-flex align-items-center gap-4">
                <TankGauge pct={tank.last?.pct} lowPct={tank.lowPct} highPct={tank.highPct} size={110} offline={!tank.online} />
                <div>
                  <div className="tank-percent big">{formatPercent(tank.last?.pct)}</div>
                  <div className="text-muted">{formatMetres(tank.last?.levelM)} water depth</div>
                  {tank.last?.volumeL != null && (
                    <div className="text-muted">
                      {formatLitres(tank.last.volumeL)} of {formatLitres(tank.capacityLitres)}
                    </div>
                  )}
                </div>
              </div>
              <ul className="list-group list-group-flush">
                <li className="list-group-item d-flex justify-content-between">
                  <span className="text-muted">Battery</span>
                  <BatteryLevel pct={tank.last?.battery} low={tank.last?.battery != null && tank.last.battery <= tank.batteryLowPct} />
                </li>
                <li className="list-group-item d-flex justify-content-between">
                  <span className="text-muted">Signal</span>
                  <span>
                    <SignalBars rssi={tank.last?.rssi} />
                    {tank.last?.rssi != null && <span className="text-muted small ms-1">({tank.last.rssi} dBm)</span>}
                  </span>
                </li>
                <li className="list-group-item d-flex justify-content-between">
                  <span className="text-muted">Packets received / missed</span>
                  <span>
                    {tank.last?.successCount ?? "—"} / {tank.last?.failCount ?? "—"}
                  </span>
                </li>
                <li className="list-group-item d-flex justify-content-between">
                  <span className="text-muted">Last reading</span>
                  <span title={formatDateTime(tank.last?.ts)}>{timeAgo(tank.last?.ts)}</span>
                </li>
                <li className="list-group-item d-flex justify-content-between">
                  <span className="text-muted">Tank depth / alarms</span>
                  <span>
                    {tank.tankDepthM ?? "—"} m · {tank.lowPct ?? "—"}% / {tank.highPct ?? "—"}%
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card">
              <div className="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <div>
                  <h5 className="mb-0">Level history, % full</h5>
                  <div className="text-muted small">Averaged per interval · dashed lines are the alarm levels</div>
                </div>
                <div className="btn-group btn-group-sm" role="group" aria-label="Time range">
                  {RANGES.map((r) => (
                    <button key={r} type="button" className={`btn ${r === range ? "btn-primary" : "btn-outline-secondary"}`} onClick={() => setRange(r)}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              <div className="card-body">
                {chartLoading ? <div className="text-center text-muted py-5">Loading…</div> : <LevelChart points={points} range={range} tank={tank} />}
                <div className="d-flex gap-3 mt-2">
                  <button className="btn btn-link p-0" onClick={() => setShowTable(!showTable)}>
                    <i className="ti ti-table me-1"></i>
                    {showTable ? "Hide table" : "Show as table"}
                  </button>
                  <button className="btn btn-link p-0" onClick={handleExport}>
                    <i className="ti ti-download me-1"></i> Download CSV
                  </button>
                </div>
                {showTable && (
                  <div className="mt-3">
                    <DataTable value={[...points].reverse()} paginator rows={10} size="small" stripedRows>
                      <Column header="Time" body={(p) => formatDateTime(p.ts)} />
                      <Column field="pct" header="% full" />
                      <Column field="levelM" header="Depth (m)" />
                      <Column field="battery" header="Battery %" />
                      <Column field="rssi" header="Signal (dBm)" />
                    </DataTable>
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent alerts</h5>
                <Link to={waterSensorsTabPath("alerts")} className="small">
                  View all
                </Link>
              </div>
              <div className="card-body">
                {alerts.length === 0 ? (
                  <div className="text-muted">No alerts for this tank.</div>
                ) : (
                  <DataTable value={alerts} size="small">
                    <Column
                      header="Alert"
                      body={(a) => (
                        <span className={`badge bg-light-${a.active ? ALERT_BADGES[a.type] : "secondary"} text-${a.active ? ALERT_BADGES[a.type] : "secondary"}`}>
                          {ALERT_LABELS[a.type]}
                        </span>
                      )}
                    />
                    <Column field="message" header="Details" />
                    <Column header="Raised" body={(a) => timeAgo(a.createdAt)} />
                    <Column header="Status" body={(a) => (a.active ? "Active" : `Resolved ${timeAgo(a.resolvedAt)}`)} />
                  </DataTable>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit settings */}
        <Dialog header="Tank settings" visible={showEditModal} style={{ width: "min(640px, 95vw)" }} onHide={() => setShowEditModal(false)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveSettings();
            }}
          >
            <div className="row">
              {SETTINGS_FIELDS.map((f) => (
                <div className="col-md-6 mb-3" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input
                    type={f.type}
                    step={f.step}
                    className="form-control"
                    value={editForm[f.key] ?? ""}
                    onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                  />
                  {f.hint && <div className="form-text">{f.hint}</div>}
                </div>
              ))}
            </div>
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="alertsEnabled"
                checked={!!editForm.alertsEnabled}
                onChange={(e) => setEditForm({ ...editForm, alertsEnabled: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="alertsEnabled">
                Alerts enabled for this tank
              </label>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Save
              </button>
            </div>
          </form>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog header="Delete tank" visible={showDeleteModal} style={{ width: "min(460px, 95vw)" }} onHide={() => setShowDeleteModal(false)}>
          <p>
            Delete <b>{tank.name}</b> and all its readings and alerts? If the terminal keeps reporting, it will reappear as a new tank.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-outline-secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDeleteTank}>
              Delete
            </button>
          </div>
        </Dialog>
      </div>
    </Layout>
  );
}

export default ViewTank;
